import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Button,
  Card,
  Empty,
  Form,
  Input,
  message,
  Modal,
  Popconfirm,
  Result,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tag,
  Tooltip,
  Typography,
  Divider
} from 'antd'
import {
  DeleteOutlined,
  EditOutlined,
  FileProtectOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  MinusCircleOutlined
} from '@ant-design/icons'
import styled from 'styled-components'
import { Radio } from 'antd'
import { useAuth } from '../hooks/AuthContext'
import { useMembership } from '../hooks/useMembership'
import api, { getApiErrorMessage } from '../utils/api'
import {
  createConsentFormTemplate,
  deleteConsentFormTemplate,
  listConsentFormTemplates,
  updateConsentFormTemplate
} from '../api/consentForm'
import Hint from '../components/common/Hint'

const { Title, Text } = Typography

const PageWrapper = styled.div`
  min-height: calc(100vh - 120px);
  padding: 24px;
  background: #f5f5f5;

  @media (max-width: 768px) {
    padding: 12px;
  }
`

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const HeaderCard = styled(Card)`
  border-radius: 16px;
  border: 1px solid #eef0f5;
  box-shadow: 0 6px 18px rgba(15, 37, 98, 0.05);

  .ant-card-body {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    align-items: center;
    justify-content: space-between;
  }

  @media (max-width: 768px) {
    .ant-card-body {
      flex-direction: column;
      align-items: stretch;
      justify-content: center;
      text-align: center;
    }

    .ant-card-body > .ant-space {
      justify-content: center;
      width: 100%;
    }
  }
`

const TableCard = styled(Card)`
  border-radius: 16px;
  border: 1px solid #eef0f5;
  box-shadow: 0 6px 18px rgba(15, 37, 98, 0.05);
`

const ToolBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
`

const DesktopTable = styled(Table)`
  @media (max-width: 768px) {
    display: none;
  }
`

const MobileList = styled.div`
  display: none;
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 10px;
  }
`

const MobileItemCard = styled.div`
  background: #fff;
  border: 1px solid #eef0f5;
  border-radius: 12px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const MobileTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
`

const MobileTitle = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #2c2531;
  flex: 1;
  word-break: break-all;
`

const MobileActionRow = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 4px;

  .ant-btn {
    flex: 1;
  }
`

const CheckboxRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`

/* 一題一張卡：題目、題型、選項要黏在一起看，散成一排會分不出選項屬於哪一題 */
const QuestionCard = styled.div`
  border: 1px solid #f0e4e8;
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 10px;
  background: #fffdfd;
`

const QuestionHead = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  flex-wrap: wrap;
`

const OptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  padding-left: 12px;
`

const SwitchGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 8px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    row-gap: 16px;
  }
`

const CONSENT_COLOR = '#5b8def'

const TIMING_LABEL = {
  on_booking: '預約成立即發',
  before_visit: '行前發送',
  manual: '僅手動'
}

/**
 * 同一位顧客要簽幾次。顧客資料表、初診問卷這類建檔一次就好的表單，
 * 每次預約都跳會讓熟客很煩（W美胸「美胸顧客資料表」），所以讓店家自己選。
 */
const FREQUENCY_LABEL = {
  every_booking: '每次預約都發',
  once: '每位顧客簽一次',
  new_customer: '只發給新客'
}

const ConsentFormSettingPage = () => {
  const { user } = useAuth()
  const { hasFeature, permissionsLoaded } = useMembership()
  const businessId = user?.businessId

  const [loading, setLoading] = useState(false)
  const [keyword, setKeyword] = useState('')
  const [includeInactive, setIncludeInactive] = useState(false)
  const [templates, setTemplates] = useState([])

  const [services, setServices] = useState([])
  const [servicesLoading, setServicesLoading] = useState(false)
  // 堂票範本（購買同意書要限定「買哪一種堂票才發」時用）
  const [ticketTemplates, setTicketTemplates] = useState([])
  const [ticketTemplatesLoading, setTicketTemplatesLoading] = useState(false)

  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [form] = Form.useForm()
  const [submitting, setSubmitting] = useState(false)

  const featureEnabled = hasFeature('consentForm')
  const watchedFormType = Form.useWatch('formType', form) || 'treatment'
  // 必須在元件頂層呼叫 —— 寫在 JSX 的條件區塊裡會變成「有時呼叫有時不呼叫」，
  // 違反 hooks 規則，切換用途時會整頁壞掉
  const watchedPurchaseTargets = Form.useWatch('purchaseTargets', form) || []
  const contentRef = useRef(null)

  // 可插入的變數（中文按鈕 → 對應 token）
  const VARIABLE_BUTTONS = [
    { label: '方案名', token: '{{planName}}' },
    { label: '到期日', token: '{{expiryDate}}' },
    { label: '金額', token: '{{amount}}' },
    { label: '售價', token: '{{price}}' },
    { label: '客戶姓名', token: '{{customerName}}' }
  ]

  // 於游標處插入變數 token（無游標則附加到最後）
  const insertVariable = (token) => {
    const current = form.getFieldValue('content') || ''
    const el = contentRef.current?.resizableTextArea?.textArea
    let start = current.length
    let end = current.length
    if (el && typeof el.selectionStart === 'number') {
      start = el.selectionStart
      end = el.selectionEnd
    }
    const next = current.slice(0, start) + token + current.slice(end)
    form.setFieldsValue({ content: next })
    // 還原游標到插入後位置
    setTimeout(() => {
      if (el) {
        const pos = start + token.length
        el.focus()
        el.setSelectionRange(pos, pos)
      }
    }, 0)
  }

  const fetchServices = useCallback(async () => {
    if (!businessId) return
    setServicesLoading(true)
    try {
      const res = await api.get(`/api/business_B/${businessId}/products`)
      const list = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.data)
          ? res.data.data
          : []
      setServices(
        list.map((p) => ({
          _id: p._id,
          name: p.name
        }))
      )
    } catch (err) {
      console.warn('fetch services failed', err)
      setServices([])
    } finally {
      setServicesLoading(false)
    }
  }, [businessId])

  // 堂票範本清單。非品牌方案會回 403，屬正常，靜默略過即可。
  const fetchTicketTemplates = useCallback(async () => {
    if (!businessId) return
    setTicketTemplatesLoading(true)
    try {
      const res = await api.get(
        `/api/businesses/${businessId}/class-tickets/templates`,
        { params: { includeInactive: '1' } }
      )
      const list = res?.data?.data || []
      setTicketTemplates(
        list.map((t) => ({ _id: t._id, name: t.name, isActive: t.isActive }))
      )
    } catch (err) {
      setTicketTemplates([])
    } finally {
      setTicketTemplatesLoading(false)
    }
  }, [businessId])

  const fetchTemplates = useCallback(async () => {
    if (!businessId) return
    setLoading(true)
    try {
      const res = await listConsentFormTemplates(businessId, {
        includeInactive: includeInactive ? 'true' : 'false'
      })
      let list = res?.data?.data || []
      if (keyword) {
        const kw = keyword.toLowerCase()
        list = list.filter((t) => (t.title || '').toLowerCase().includes(kw))
      }
      setTemplates(list)
    } catch (err) {
      message.error(getApiErrorMessage(err, '取得同意書範本失敗'))
    } finally {
      setLoading(false)
    }
  }, [businessId, keyword, includeInactive])

  useEffect(() => {
    fetchServices()
    fetchTicketTemplates()
  }, [fetchServices, fetchTicketTemplates])

  useEffect(() => {
    if (featureEnabled) fetchTemplates()
  }, [fetchTemplates, featureEnabled])

  const serviceMap = useMemo(() => {
    const m = new Map()
    services.forEach((s) => m.set(String(s._id), s))
    return m
  }, [services])

  const openCreate = () => {
    setEditing(null)
    form.resetFields()
    form.setFieldsValue({
      formType: 'treatment',
      purchaseTargets: [],
      applicableClassTicketTemplateIds: [],
      isActive: true,
      requireSignature: true,
      requireIdNumber: false,
      requireBirthdate: false,
      noExpiry: false,
      sendTiming: 'before_visit',
      signFrequency: 'every_booking',
      applicableServiceIds: [],
      checkboxes: [{ label: '本人已充分閱讀並同意上述內容', required: true }],
      questions: []
    })
    setModalOpen(true)
  }

  const openEdit = (record) => {
    setEditing(record)
    form.setFieldsValue({
      formType: record.formType || 'treatment',
      purchaseTargets: record.purchaseTargets || [],
      applicableClassTicketTemplateIds:
        (record.applicableClassTicketTemplateIds || []).map(String),
      title: record.title,
      content: record.content,
      checkboxes: (record.checkboxes || []).map((c) => ({
        key: c.key,
        label: c.label,
        required: c.required !== false
      })),
      questions: (record.questions || []).map((q) => ({
        key: q.key,
        type: q.type || 'single',
        label: q.label,
        required: q.required !== false,
        placeholder: q.placeholder || '',
        options: (q.options || []).map((o) => ({ key: o.key, label: o.label }))
      })),
      requireSignature: record.requireSignature !== false,
      requireIdNumber: !!record.requireIdNumber,
      requireBirthdate: !!record.requireBirthdate,
      noExpiry: record.noExpiry === true,
      sendTiming: record.sendTiming || 'before_visit',
      signFrequency: record.signFrequency || 'every_booking',
      applicableServiceIds: record.applicableServiceIds || [],
      description: record.description || '',
      isActive: record.isActive !== false
    })
    setModalOpen(true)
  }

  const submitForm = async () => {
    try {
      const values = await form.validateFields()
      // checkboxes 轉為後端格式（補 key）
      const checkboxes = (values.checkboxes || []).map((c, i) => ({
        key: c.key || `item_${i + 1}`,
        label: c.label,
        required: c.required !== false
      }))
      // questions 轉為後端格式：補 key（答案存的是 key，既有 key 一律沿用，
      // 改題目文字才不會讓已簽紀錄對不上）；text 題不留選項
      const questions = (values.questions || []).map((q, i) => {
        const type = q.type || 'single'
        return {
          key: q.key || `q_${i + 1}_${Date.now().toString(36)}`,
          type,
          label: q.label,
          required: q.required !== false,
          placeholder: type === 'text' ? q.placeholder || '' : '',
          options:
            type === 'text'
              ? []
              : (q.options || []).map((o, j) => ({
                  key: o.key || `opt_${j + 1}`,
                  label: o.label
                }))
        }
      })
      // 選擇題沒有選項就送不出去 —— 客戶會看到一題沒有任何可選的東西
      const emptyOptionQuestion = questions.find(
        (q) => q.type !== 'text' && q.options.length === 0
      )
      if (emptyOptionQuestion) {
        message.error(`「${emptyOptionQuestion.label}」至少要有一個選項`)
        return
      }
      const payload = { ...values, checkboxes, questions }
      setSubmitting(true)
      if (editing) {
        await updateConsentFormTemplate(businessId, editing._id, payload)
        message.success('範本已更新')
      } else {
        await createConsentFormTemplate(businessId, payload)
        message.success('範本已新增')
      }
      setModalOpen(false)
      fetchTemplates()
    } catch (err) {
      if (err && err.errorFields) return
      message.error(getApiErrorMessage(err, '儲存失敗'))
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (record) => {
    try {
      await deleteConsentFormTemplate(businessId, record._id)
      message.success('已刪除')
      fetchTemplates()
    } catch (err) {
      message.error(getApiErrorMessage(err, '刪除失敗'))
    }
  }

  if (!permissionsLoaded) {
    return (
      <PageWrapper>
        <Spin />
      </PageWrapper>
    )
  }
  if (!featureEnabled) {
    return (
      <PageWrapper>
        <Result
          status='warning'
          title='您的方案尚未開通同意書模組'
          subTitle='請升級至高階方案以使用項目施作同意書功能。'
        />
      </PageWrapper>
    )
  }

  const columns = [
    {
      title: '同意書名稱',
      dataIndex: 'title',
      key: 'title',
      render: (v) => <Text strong>{v}</Text>
    },
    {
      title: '用途',
      dataIndex: 'formType',
      key: 'formType',
      width: 110,
      render: (v) =>
        v === 'purchase' ? (
          <Tag color='purple'>購買同意書</Tag>
        ) : (
          <Tag color='blue'>施作同意書</Tag>
        )
    },
    {
      title: '版本',
      dataIndex: 'version',
      key: 'version',
      width: 80,
      align: 'center',
      render: (v) => `v${v || 1}`
    },
    {
      title: '簽名方式',
      dataIndex: 'requireSignature',
      key: 'requireSignature',
      width: 110,
      render: (v) =>
        v ? <Tag color='blue'>手寫簽名</Tag> : <Tag>勾選同意</Tag>
    },
    {
      title: '發送時機',
      dataIndex: 'sendTiming',
      key: 'sendTiming',
      width: 120,
      render: (v, record) => (
        <Space size={[4, 4]} wrap>
          <span>{TIMING_LABEL[v] || v}</span>
          {(record.formType || 'treatment') === 'treatment' &&
            record.signFrequency &&
            record.signFrequency !== 'every_booking' && (
              <Tag color='magenta'>{FREQUENCY_LABEL[record.signFrequency]}</Tag>
            )}
        </Space>
      )
    },
    {
      title: '適用服務',
      dataIndex: 'applicableServiceIds',
      key: 'applicableServiceIds',
      render: (ids) => {
        if (!ids || ids.length === 0) return <Tag>不限</Tag>
        return (
          <Space size={[4, 4]} wrap>
            {ids.slice(0, 3).map((id) => (
              <Tag key={String(id)}>
                {serviceMap.get(String(id))?.name || '已刪除服務'}
              </Tag>
            ))}
            {ids.length > 3 && <Tag>+{ids.length - 3}</Tag>}
          </Space>
        )
      }
    },
    {
      title: '狀態',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 90,
      render: (v) => (v ? <Tag color='green'>啟用</Tag> : <Tag>停用</Tag>)
    },
    {
      title: '操作',
      key: 'actions',
      width: 90,
      fixed: 'right',
      align: 'center',
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title='編輯'>
            <Button
              type='text'
              size='small'
              icon={<EditOutlined />}
              onClick={() => openEdit(record)}
            />
          </Tooltip>
          <Popconfirm
            title='確定刪除此範本？'
            description='已發送的同意書不受影響。'
            okText='刪除'
            okType='danger'
            cancelText='取消'
            onConfirm={() => handleDelete(record)}
          >
            <Tooltip title='刪除'>
              <Button type='text' size='small' danger icon={<DeleteOutlined />} />
            </Tooltip>
          </Popconfirm>
        </Space>
      )
    }
  ]

  return (
    <PageWrapper>
      <ContentWrapper>
        <HeaderCard>
          <Space size='middle' align='center'>
            <FileProtectOutlined style={{ fontSize: 22, color: CONSENT_COLOR }} />
            <Title level={4} style={{ margin: 0 }}>
              同意書設定
            </Title>
          </Space>
          <Space>
            <Button icon={<ReloadOutlined />} onClick={fetchTemplates}>
              重新整理
            </Button>
            <Button type='primary' icon={<PlusOutlined />} onClick={openCreate}>
              新增同意書
            </Button>
          </Space>
        </HeaderCard>

        <TableCard>
          <ToolBar>
            <Input
              allowClear
              placeholder='輸入同意書名稱搜尋'
              prefix={<SearchOutlined />}
              style={{ width: 280 }}
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              onPressEnter={fetchTemplates}
            />
            <Space>
              <Switch checked={includeInactive} onChange={setIncludeInactive} />
              <Text type='secondary'>顯示已停用</Text>
            </Space>
          </ToolBar>

          <DesktopTable
            rowKey='_id'
            columns={columns}
            dataSource={templates}
            loading={loading}
            scroll={{ x: 800 }}
            locale={{ emptyText: <Empty description='尚未建立同意書範本' /> }}
            pagination={{ pageSize: 20, showSizeChanger: false }}
          />

          <MobileList>
            {loading ? (
              <Spin />
            ) : templates.length === 0 ? (
              <Empty description='尚未建立同意書範本' />
            ) : (
              templates.map((record) => (
                <MobileItemCard key={record._id}>
                  <MobileTitleRow>
                    <MobileTitle>{record.title}</MobileTitle>
                    {record.isActive !== false ? (
                      <Tag color='green'>啟用</Tag>
                    ) : (
                      <Tag>停用</Tag>
                    )}
                  </MobileTitleRow>
                  <Space size={[4, 4]} wrap>
                    <Tag color='blue'>
                      {record.requireSignature !== false ? '手寫簽名' : '勾選同意'}
                    </Tag>
                    <Tag>{TIMING_LABEL[record.sendTiming] || record.sendTiming}</Tag>
                    {(record.formType || 'treatment') === 'treatment' &&
                      record.signFrequency &&
                      record.signFrequency !== 'every_booking' && (
                        <Tag color='magenta'>
                          {FREQUENCY_LABEL[record.signFrequency]}
                        </Tag>
                      )}
                    <Tag>v{record.version || 1}</Tag>
                  </Space>
                  <MobileActionRow>
                    <Button icon={<EditOutlined />} onClick={() => openEdit(record)}>
                      編輯
                    </Button>
                    <Popconfirm
                      title='確定刪除此範本？'
                      description='已發送的同意書不受影響。'
                      okText='刪除'
                      okType='danger'
                      cancelText='取消'
                      onConfirm={() => handleDelete(record)}
                    >
                      <Button danger icon={<DeleteOutlined />}>
                        刪除
                      </Button>
                    </Popconfirm>
                  </MobileActionRow>
                </MobileItemCard>
              ))
            )}
          </MobileList>
        </TableCard>
      </ContentWrapper>

      <Modal
        open={modalOpen}
        title={editing ? '編輯同意書範本' : '新增同意書範本'}
        okText='儲存'
        cancelText='取消'
        onOk={submitForm}
        onCancel={() => setModalOpen(false)}
        confirmLoading={submitting}
        destroyOnClose
        width={680}
      >
        <Form form={form} layout='vertical'>
          <Form.Item label='用途' name='formType'>
            <Radio.Group
              optionType='button'
              buttonStyle='solid'
              options={[
                { label: '施作同意書（綁預約，行前簽）', value: 'treatment' },
                { label: '購買同意書（綁堂票/儲值，購買時簽）', value: 'purchase' }
              ]}
            />
          </Form.Item>

          {watchedFormType === 'purchase' && (
            <>
              <Form.Item
                label='適用購買行為'
                name='purchaseTargets'
                rules={[{ required: true, message: '請至少選一種購買行為' }]}
              >
                <Select
                  mode='multiple'
                  placeholder='此同意書於哪些購買時出現'
                  options={[
                    { label: '發放堂票時', value: 'classTicket' },
                    { label: '儲值時', value: 'balance' }
                  ]}
                />
              </Form.Item>
              {watchedPurchaseTargets.includes('classTicket') && (
                <Form.Item
                  label='限定堂票（不選代表所有堂票都要簽）'
                  name='applicableClassTicketTemplateIds'
                  extra='只有購買這裡選中的堂票時才會發送此同意書。常見用法：只有高單價療程需要簽權益聲明，一般小堂票不必。'
                >
                  <Select
                    mode='multiple'
                    placeholder='選擇此同意書適用的堂票'
                    loading={ticketTemplatesLoading}
                    optionFilterProp='label'
                    options={ticketTemplates.map((t) => ({
                      label: t.isActive === false ? `${t.name}（已停用）` : t.name,
                      value: t._id
                    }))}
                    maxTagCount='responsive'
                    allowClear
                    notFoundContent={
                      ticketTemplatesLoading ? '載入中…' : '尚未建立堂票範本'
                    }
                  />
                </Form.Item>
              )}
              <Hint
                tone='info'
                style={{ marginBottom: 16 }}
                title='購買同意書為「提醒」性質，不會阻擋購買'
              >
                {'購買完成後會提醒店家可傳送給客人簽署或現場開給客人簽。條款可用變數（購買時自動代入）：{{planName}} 方案名、{{expiryDate}} 到期日、{{amount}} 金額、{{price}} 售價、{{customerName}} 客戶姓名。'}
              </Hint>
            </>
          )}

          <Form.Item
            label='同意書名稱'
            name='title'
            rules={[{ required: true, message: '請輸入名稱' }]}
          >
            <Input placeholder='例：醫美雷射施作同意書 / 購買確認暨權益聲明' maxLength={80} />
          </Form.Item>
          <Form.Item
            label='條款內文'
            name='content'
            rules={[{ required: true, message: '請輸入條款內文' }]}
            extra={
              watchedFormType === 'purchase'
                ? '顯示給客戶閱讀，支援換行；點下方按鈕可於游標處插入變數。'
                : '顯示給客戶閱讀，支援換行。'
            }
          >
            <Input.TextArea
              ref={contentRef}
              rows={6}
              maxLength={5000}
              placeholder='請輸入完整同意書條款…'
            />
          </Form.Item>
          {watchedFormType === 'purchase' && (
            <div style={{ marginTop: -8, marginBottom: 16 }}>
              <Text type='secondary' style={{ fontSize: 12, marginRight: 8 }}>
                插入變數：
              </Text>
              <Space size={[8, 8]} wrap>
                {VARIABLE_BUTTONS.map((v) => (
                  <Button
                    key={v.token}
                    size='small'
                    onClick={() => insertVariable(v.token)}
                  >
                    {v.label}
                  </Button>
                ))}
              </Space>
            </div>
          )}

          <Divider orientation='left' plain>
            需客戶勾選的同意項目
          </Divider>
          <Form.List name='checkboxes'>
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...rest }) => (
                  <CheckboxRow key={key}>
                    <Form.Item name={[name, 'key']} hidden {...rest}>
                      <Input />
                    </Form.Item>
                    <Form.Item
                      {...rest}
                      name={[name, 'label']}
                      rules={[{ required: true, message: '請輸入項目文字' }]}
                      style={{ marginBottom: 0, flex: 1, minWidth: 0 }}
                    >
                      <Input placeholder='例：本人已了解本次施作之風險與注意事項' />
                    </Form.Item>
                    <Form.Item
                      {...rest}
                      name={[name, 'required']}
                      valuePropName='checked'
                      style={{ marginBottom: 0 }}
                    >
                      <Switch checkedChildren='必勾' unCheckedChildren='選填' />
                    </Form.Item>
                    <MinusCircleOutlined
                      style={{ flexShrink: 0, color: '#bbb', fontSize: 18 }}
                      onClick={() => remove(name)}
                    />
                  </CheckboxRow>
                ))}
                <Button
                  type='dashed'
                  onClick={() => add({ label: '', required: true })}
                  block
                  icon={<PlusOutlined />}
                >
                  新增勾選項目
                </Button>
              </>
            )}
          </Form.List>

          <Divider orientation='left' plain>
            問卷題目（選填）
          </Divider>
          <Hint tone='info' title='勾選項目與問卷題目的差別'>
            勾選項目是「請客戶同意某件事」，只有勾或不勾；問卷題目是「向客戶問一件事」，
            例如術前的用藥或手術史，答案會跟著這份同意書一起存證，也會出現在客戶資料裡。
          </Hint>
          <Form.List name='questions'>
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...rest }) => (
                  <QuestionCard key={key}>
                    <Form.Item name={[name, 'key']} hidden {...rest}>
                      <Input />
                    </Form.Item>
                    <QuestionHead>
                      <Form.Item
                        {...rest}
                        name={[name, 'label']}
                        rules={[{ required: true, message: '請輸入題目' }]}
                        style={{ marginBottom: 8, flex: '1 1 260px', minWidth: 0 }}
                      >
                        <Input placeholder='例：有正在服用中的藥物嗎？' />
                      </Form.Item>
                      <Form.Item
                        {...rest}
                        name={[name, 'type']}
                        style={{ marginBottom: 8, width: 120 }}
                      >
                        <Select
                          options={[
                            { label: '單選', value: 'single' },
                            { label: '多選', value: 'multiple' },
                            { label: '簡答', value: 'text' }
                          ]}
                        />
                      </Form.Item>
                      <Form.Item
                        {...rest}
                        name={[name, 'required']}
                        valuePropName='checked'
                        style={{ marginBottom: 8 }}
                      >
                        <Switch checkedChildren='必填' unCheckedChildren='選填' />
                      </Form.Item>
                      <MinusCircleOutlined
                        style={{
                          flexShrink: 0,
                          color: '#bbb',
                          fontSize: 18,
                          marginTop: 8
                        }}
                        onClick={() => remove(name)}
                      />
                    </QuestionHead>

                    {/* 題型決定下面要顯示選項清單還是提示文字，
                        所以這裡要跟著同一題的 type 重繪 */}
                    <Form.Item
                      noStyle
                      shouldUpdate={(prev, cur) =>
                        prev.questions?.[name]?.type !==
                        cur.questions?.[name]?.type
                      }
                    >
                      {({ getFieldValue }) => {
                        const type =
                          getFieldValue(['questions', name, 'type']) || 'single'
                        if (type === 'text') {
                          return (
                            <Form.Item
                              {...rest}
                              name={[name, 'placeholder']}
                              style={{ marginBottom: 0, paddingLeft: 12 }}
                            >
                              <Input
                                placeholder='作答欄的提示文字（例：請說明藥物名稱與服用期間）'
                                maxLength={50}
                              />
                            </Form.Item>
                          )
                        }
                        return (
                          <Form.List name={[name, 'options']}>
                            {(optFields, optOps) => (
                              <>
                                {optFields.map((opt) => (
                                  <OptionRow key={opt.key}>
                                    <Form.Item
                                      name={[opt.name, 'key']}
                                      hidden
                                    >
                                      <Input />
                                    </Form.Item>
                                    <Form.Item
                                      name={[opt.name, 'label']}
                                      rules={[
                                        { required: true, message: '請輸入選項' }
                                      ]}
                                      style={{
                                        marginBottom: 0,
                                        flex: 1,
                                        minWidth: 0
                                      }}
                                    >
                                      <Input placeholder='例：避孕藥' />
                                    </Form.Item>
                                    <MinusCircleOutlined
                                      style={{
                                        flexShrink: 0,
                                        color: '#bbb',
                                        fontSize: 16
                                      }}
                                      onClick={() => optOps.remove(opt.name)}
                                    />
                                  </OptionRow>
                                ))}
                                <Button
                                  type='link'
                                  size='small'
                                  icon={<PlusOutlined />}
                                  onClick={() => optOps.add({ label: '' })}
                                  style={{ paddingLeft: 12 }}
                                >
                                  新增選項
                                </Button>
                              </>
                            )}
                          </Form.List>
                        )
                      }}
                    </Form.Item>
                  </QuestionCard>
                ))}
                <Button
                  type='dashed'
                  onClick={() =>
                    add({
                      type: 'single',
                      label: '',
                      required: true,
                      options: [{ label: '' }, { label: '' }]
                    })
                  }
                  block
                  icon={<PlusOutlined />}
                >
                  新增問卷題目
                </Button>
              </>
            )}
          </Form.List>

          <Divider orientation='left' plain>
            簽署與發送設定
          </Divider>
          <SwitchGrid>
            <Form.Item label='需手寫簽名' name='requireSignature' valuePropName='checked' style={{ marginBottom: 0 }}>
              <Switch />
            </Form.Item>
            <Form.Item label='需填身分證字號' name='requireIdNumber' valuePropName='checked' style={{ marginBottom: 0 }}>
              <Switch />
            </Form.Item>
            <Form.Item label='需填生日' name='requireBirthdate' valuePropName='checked' style={{ marginBottom: 0 }}>
              <Switch />
            </Form.Item>
            <Form.Item label='啟用' name='isActive' valuePropName='checked' style={{ marginBottom: 0 }}>
              <Switch />
            </Form.Item>
          </SwitchGrid>
          {watchedFormType === 'treatment' && (
            <>
              <Form.Item label='發送時機' name='sendTiming'>
                <Select
                  options={[
                    { label: '行前自動發送（前一日提醒時）', value: 'before_visit' },
                    { label: '預約成立即發', value: 'on_booking' },
                    { label: '僅手動發送', value: 'manual' }
                  ]}
                />
              </Form.Item>
              <Form.Item
                label='簽署期限'
                name='noExpiry'
                extra='改成「不設期限」後，已發出還沒簽的（含已逾期的）也會一起改成可簽。'
              >
                <Radio.Group style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <Radio value={false}>
                    預約後自動失效
                    <span style={{ color: '#86868b', fontSize: 12, marginLeft: 6 }}>
                      預約開始 2 小時後失效，且發送後至少保留 24 小時
                    </span>
                  </Radio>
                  <Radio value={true}>
                    不設期限
                    <span style={{ color: '#86868b', fontSize: 12, marginLeft: 6 }}>
                      簽署前一直有效，適合顧客資料表、問卷這類晚點補簽也沒關係的
                    </span>
                  </Radio>
                </Radio.Group>
              </Form.Item>
              <Form.Item
                label='簽署次數'
                name='signFrequency'
                extra='只影響預約時自動發送；在預約詳情手動選這份同意書補發，不受這個設定限制。'
              >
                <Radio.Group style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <Radio value='every_booking'>每次預約都發</Radio>
                  <Radio value='once'>
                    每位顧客簽一次就好
                    <span style={{ color: '#86868b', fontSize: 12, marginLeft: 6 }}>
                      簽過就不再發；從沒簽過的舊客，下次預約仍會收到
                    </span>
                  </Radio>
                  <Radio value='new_customer'>
                    只發給新客
                    <span style={{ color: '#86868b', fontSize: 12, marginLeft: 6 }}>
                      第一次來店預約、且沒簽過的顧客才會收到
                    </span>
                  </Radio>
                </Radio.Group>
              </Form.Item>
              <Form.Item
                label='適用服務（不選代表不限服務）'
                name='applicableServiceIds'
              >
                <Select
                  mode='multiple'
                  placeholder='選擇此同意書適用的服務項目'
                  loading={servicesLoading}
                  optionFilterProp='label'
                  options={services.map((s) => ({ label: s.name, value: s._id }))}
                  maxTagCount='responsive'
                  allowClear
                />
              </Form.Item>
            </>
          )}
          <Form.Item label='內部備註' name='description'>
            <Input.TextArea rows={2} maxLength={500} placeholder='選填，僅內部可見' />
          </Form.Item>
        </Form>
      </Modal>
    </PageWrapper>
  )
}

export default ConsentFormSettingPage
